import * as yup from 'yup';
import * as personalSchema from './validations/personal';
import * as metaDataSchema from './validations/metaData';
import * as contactSchema from './validations/contact';
import * as financialSchema from './validations/financial';

export const createEmailsSchema = ({ errorMessagePrefix = '' } = {}) => yup.array().required(`${errorMessagePrefix}emails.error`).min(1)
  .of(
    yup.object()
      .shape({
        type: yup.string().oneOf(['PERSONAL'], `${errorMessagePrefix}emails.type.oneOf.error`),
        emailID: personalSchema.email,
      })
  );

export const createPhonesSchema = ({ errorMessagePrefix = '' } = {}) => yup.array().required(`${errorMessagePrefix}phones.error`).min(1)
  .of(
    yup.object()
      .shape({
        type: yup.string().oneOf(['MOBILE'], `${errorMessagePrefix}phones.type.oneOf.error`),
        number: contactSchema.mobilePhoneNumber,
        countryCode: yup.string().oneOf(['49'], `${errorMessagePrefix}phones.countryCode.oneOf.error`),
      })
  );

const createNamesSchema = ({ errorMessagePrefix = '' } = {}) => yup.array().required(`${errorMessagePrefix}names.error`).min(1)
  .of(yup.object()
    .shape({
      type: metaDataSchema.type,
      language: metaDataSchema.language,
      prefix: personalSchema.prefix,
      title: personalSchema.title,
      firstName: personalSchema.firstName,
      middleName: personalSchema.middleName,
      lastName: personalSchema.lastName,
    })
  );

const primarySchema = {
  names: createNamesSchema(),
  nameOnCard: personalSchema.nameOnCard,
  dateOfBirth: yup.string()
    .matches(/^\d{8}$/, 'dateOfBirth.error'),
  emails: createEmailsSchema(),
  phones: createPhonesSchema(),
  addresses: yup.array().required('addresses.error').min(1)
    .of(
      yup.object()
        .shape({
          type: yup.string().oneOf(['HOME'], 'addresses.type.oneOf.error'),
          language: metaDataSchema.language,
          line1: contactSchema.addressline1,
          line2: contactSchema.addressLine2,
          postalCode: contactSchema.postalCode,
          city: contactSchema.city,
          residentialStatus: contactSchema.residenceStatus,
          stayDuration: yup.string().matches(/^\d{4}$/, 'addresses.stayDuration.regex.error'),
        })
    ),

  inhabitanceInformation: yup.array().required('inhabitanceInformation.required.error')
    .of(
      yup.object()
        .shape({
          type: yup.string().oneOf(['BIRTH', 'NATIONALITY'], 'inhabitanceInformation.type.oneOf.error'),
          city: yup.string().test({
            name: 'inhabitanceInformation.city.country',
            test: function (value) {
              const { country } = this.parent;
              if(!!country === false && !!value) {
                return true;
              } else {
                return !!country;
              }
            },
            message: 'inhabitanceInformation.city.country.required.error'
          }),
          country: yup.string().test({
            name: 'inhabitanceInformation.country.city',
            test: function (value) {
              const { city } = this.parent;
              if(!!city === false && !!value) {
                return true;
              } else {
                return !!city;
              }
            },
            message: 'inhabitanceInformation.country.city.required.error'
          }),
        }),
      ),

  customerPreferences: yup.array().notRequired()
    .of(
      yup.object()
        .shape({
          type: yup.string().oneOf(['MARKETING_EMAIL', 'PHONE_MARKETING'], 'customerPreferences.type.oneOf.error'),
          options: yup.string().oneOf(['OPT_IN', 'OPT_OUT'], 'customerPreferences.options.oneOf.error'),
        })
    ),

  mothersMaidenName: contactSchema.mothersMaidenName,

  payers: yup.array().required('payers.error')
    .of(
      yup.object()
        .shape({
          number: yup.number().required(),
          provider: yup.object().shape({
            name: yup.string().oneOf(['APPLICATION_DATA'], 'payers.provider.name.oneOf.error')
          }),
          bankAccount: yup.object().shape({
            accountNumber: yup.object().shape({
              type: yup.string()
                .oneOf(['INTERNATIONAL_BANK_ACCOUNT_NUMBER'], 'payers.bankAccount.accountNumber.type.oneOf.error'),
              // TODO: need to add back in after validation library build is working.
              // number: financialSchema.iban,
            }),
            activeSince: yup.string().matches(/^\d{4}$/, 'payers.bankAccount.activeSince.regex.error'),
          }),
        })
    ),

  individualConsents: yup.array().required('individualConsents.error')
    .of(
      yup.object().shape({
        type: yup.string().oneOf(['ELECTRONIC_PAYMENT', 'TERMS'], 'individualConsents.type.oneOf.error'),
        isAccepted: yup.boolean().required('individualConsents.isAccepted.required.error'),
        consentCheckedDateTime: yup.string().notRequired(),
      })
    ),

  pin: financialSchema.pin,

  employments: yup.array().required('employments.error')
    .of(
      yup.object().shape({
        type: yup.string().oneOf(['PRIMARY'], 'employments.type.oneOf.error'),
        status: financialSchema.employmentStatus,
        occupation: yup.string().when(['status'], {
          // @ts-ignore
          is: (value: string) => financialSchema.occupationDependentValues.includes(value),
          then: () => financialSchema.occupationConditionals.required,
          otherwise: () => financialSchema.occupationConditionals.notRequired,
        }),
        durationYears: yup.number()
          .min(0, 'employments.durationYears.min.error'),
        durationMonths: yup.number()
          .min(0, 'employments.durationMonths.min.error')
          .max(11, 'employments.durationMonths.max.error'),
        industries: yup.array().notRequired()
          .of(
            yup.object().shape({
              type: yup.string().oneOf(['PRIMARY'], 'employments.industries.type.oneOf.error'),
              // NOTE: given that `industries` is not required, `industry` will only error if `industries`
              // is provided and no value is given for `industry`.
              value: financialSchema.industryConditionals.required,
            })
          ),
      })
    ),

  incomes: yup.array().notRequired()
    .of(
      yup.object().shape({
        type: yup.string().oneOf(['PRIMARY', 'OTHER'], 'incomes.type.error'),
        frequency: yup.string().oneOf(['ANNUALLY','MONTHLY'], 'incomes.frequency.error'),
        isAfterTax: yup.boolean().notRequired().default(false),
        amount: yup.number().required('incomes.amount.required.error'),
        source: yup.string().oneOf(financialSchema.sourceOfIncomeValues),
      })
    ),

  supplementaryApplicants: yup.array().notRequired()
    .of(
      yup.object().shape({
        plasticColor: yup.string().notRequired().oneOf(financialSchema.allowedPlasticColors, 'supplementaryApplicants.plasticColor.oneOf.error'),
        names: createNamesSchema({ errorMessagePrefix: 'supplementaryApplicants.' }),
        nameOnCard: personalSchema.nameOnCard,
        dateOfBirth: yup.string()
          .matches(/^\d{8}$/, 'dateOfBirth.error'),
        emails: createEmailsSchema({ errorMessagePrefix: 'supplementaryApplicants.' }),
        phones: createPhonesSchema({ errorMessagePrefix: 'supplementaryApplicants.' }),

        employments: yup.array().required('supplementaryApplicants.employments.error')
          .of(
            yup.object().shape({
              type: yup.string().oneOf(['PRIMARY'], 'supplementaryApplicants.employments.type.oneOf.error'),
              status: financialSchema.employmentStatus,
              // NOTE: add back in when we get a story for these fields
              //       the fields are already added to the test data.
              // occupation: yup.string().when(['status'], {
              //   // @ts-ignore
              //   is: (value: string) => financialSchema.occupationDependentValues.includes(value),
              //   then: () => financialSchema.occupationConditionals.required,
              //   otherwise: () => financialSchema.occupationConditionals.notRequired,
              // }),
              // industries: yup.array().notRequired()
              //   .of(
              //     yup.object().shape({
              //       type: yup.string().oneOf(['PRIMARY'], 'supplementaryApplicants.employments.industries.type.oneOf.error'),
              //       // NOTE: given that `industries` is not required, `industry` will only error if `industries`
              //       // is provided and no value is given for `industry`.
              //       value: financialSchema.industryConditionals.required,
              //     })
              //   ),
            })
          ),

        hasSameAddressAsPrimary: yup.boolean().required('supplementaryApplicants.hasSameAddressAsPrimary.required.error'),

        addresses: yup.array()
          .when(['hasSameAddressAsPrimary'], {
            is: false,
            then: (schema) => schema.required('supplementaryApplicants.addresses.error').min(1)
              .of(
                yup.object()
                  .shape({
                    type: yup.string().oneOf(['HOME'], 'supplementaryApplicants.addresses.type.oneOf.error'),
                    language: metaDataSchema.language,
                    line1: contactSchema.addressline1,
                    line2: contactSchema.addressLine2,
                    postalCode: contactSchema.postalCode,
                    city: contactSchema.city,
                  })
              ),
            otherwise: (schema) => schema.notRequired(),
          }),

        inhabitanceInformation: yup.array().required('supplementaryApplicants.inhabitanceInformation.required.error')
          .of(
            yup.object()
              .shape({
                type: yup.string().oneOf(['BIRTH', 'NATIONALITY'], 'supplementaryApplicants.inhabitanceInformation.type.oneOf.error'),
                city: yup.string().test({
                  name: 'inhabitanceInformation.city.country',
                  test: function (value) {
                    const { country } = this.parent;
                    if(!!country === false && !!value) {
                      return true;
                    } else {
                      return !!country;
                    }
                  },
                  message: 'supplementaryApplicants.inhabitanceInformation.city.country.required.error'
                }),
                country: yup.string().test({
                  name: 'inhabitanceInformation.country.city',
                  test: function (value) {
                    const { city } = this.parent;
                    if(!!city === false && !!value) {
                      return true;
                    } else {
                      return !!city;
                    }
                  },
                  message: 'supplementaryApplicants.inhabitanceInformation.country.city.required.error'
                }),
              }),
            ),
      })
    ),
};

export const goldPrimarySchema = {
  ...primarySchema,
  insuranceEnrollmentPreference: yup.boolean().notRequired(),
};

export default primarySchema;
